import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { EmployerCandidateDirectorySummary } from '@tabbio/types';
import { useEmployerCandidates } from '../../hooks/useEmployerCandidatesApi';
import { JobsSkeleton } from './JobsSkeleton.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { UserAvatar } from '../common/UserAvatar';

interface EmployerJobMatchedCandidatesSectionProps {
  jobId: string;
  headerSlot?: ReactNode;
}

export function EmployerJobMatchedCandidatesSection({
  jobId,
  headerSlot,
}: EmployerJobMatchedCandidatesSectionProps) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const candidatesQuery = useEmployerCandidates({ jobId, limit: 24 });

  const candidates = useMemo(
    () => candidatesQuery.data?.pages.flatMap((page) => page.candidates) ?? [],
    [candidatesQuery.data],
  );

  useEffect(() => {
    const node = sentinelRef.current;
    if (!isObservableElement(node)) return;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (
        entry?.isIntersecting &&
        candidatesQuery.hasNextPage &&
        !candidatesQuery.isFetchingNextPage
      ) {
        void candidatesQuery.fetchNextPage();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [candidatesQuery]);

  const onCardPress = (userId: string) => {
    router.push(
      `/(employer)/candidates/${userId}?from=job&jobId=${jobId}` as never,
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {headerSlot ?? null}

      {candidatesQuery.isLoading && candidates.length === 0 ? (
        <JobsSkeleton variant="feed" count={4} />
      ) : candidates.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            padding: '56px 16px',
            textAlign: 'center',
          }}
        >
          <span
            aria-hidden
            style={{ color: 'var(--text-tertiary)', marginBottom: 4 }}
          >
            <MaterialSymbol name="person_search" size={40} />
          </span>
          <div
            style={{
              fontSize: 18,
              fontWeight: 500,
              color: 'var(--text-primary)',
            }}
          >
            {t('jobDetail.matched.empty.title')}
          </div>
          <div
            style={{
              fontSize: 14,
              lineHeight: 1.5,
              color: 'var(--text-secondary)',
              maxWidth: 420,
            }}
          >
            {t('jobDetail.matched.empty.subtitle')}
          </div>
        </div>
      ) : (
        <>
          {candidates.map((candidate) => (
            <MatchedCandidateRow
              key={candidate.userId}
              candidate={candidate}
              onPress={() => onCardPress(candidate.userId)}
            />
          ))}
          <div ref={sentinelRef} style={{ height: 1 }} />
          {candidatesQuery.isFetchingNextPage ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: 16,
              }}
            >
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: '2px solid var(--gem-outline-variant)',
                  borderTopColor: 'var(--text-primary)',
                  animation: 'matchedspin 0.8s linear infinite',
                }}
              />
              <style>{`@keyframes matchedspin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function isObservableElement(node: unknown): node is Element {
  return typeof Element !== 'undefined' && node instanceof Element;
}

function MatchedCandidateRow({
  candidate,
  onPress,
}: {
  candidate: EmployerCandidateDirectorySummary;
  onPress: () => void;
}) {
  const { t } = useTranslation('common');
  const matchPercent = Math.round(candidate.matchScore * 100);
  const topSkills = (
    candidate.matchedSkills.length
      ? candidate.matchedSkills
      : candidate.skills
  ).slice(0, 4);

  return (
    <button
      type="button"
      onClick={onPress}
      className="tabbio-list-row"
      style={{
        alignItems: 'flex-start',
        minHeight: 96,
        padding: '18px 22px',
      }}
    >
      <UserAvatar size={40} />
      <div className="tabbio-list-row-body">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 15,
              fontWeight: 500,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {candidate.name}
          </span>
          {matchPercent > 0 ? (
            <span
              className="tabbio-menu-badge"
              style={{ flexShrink: 0 }}
              aria-label={t('jobDetail.matched.matchLabel')}
            >
              {matchPercent}% match
            </span>
          ) : null}
        </div>
        {candidate.title ? (
          <div className="tabbio-list-row-sub">{candidate.title}</div>
        ) : null}
        {topSkills.length ? (
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
            }}
          >
            {topSkills.map((skill) => (
              <span key={skill} className="tabbio-menu-badge">
                {skill}
              </span>
            ))}
          </div>
        ) : null}
        {candidate.outreachLockedByPlan ? (
          <span
            className="tabbio-menu-badge"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              alignSelf: 'flex-start',
              marginTop: 10,
            }}
          >
            <MaterialSymbol name="lock" size={12} />
            {t('jobDetail.matched.upgradeBadge')}
          </span>
        ) : null}
      </div>
    </button>
  );
}
